<template>
  <div class="tabs-container">
    <div class="tabs-header">
      <div class="tabs-header-tabs">
        <button 
          v-for="tab in tabs" 
          :key="tab.id"
          :class="['tab-button', { active: activeTab === tab.id }]"
          @click="switchTab(tab.id)">
          <slot :name="`tab-header-${tab.id}`" :tab="tab" :isActive="activeTab === tab.id">
            {{ tab.label }}
          </slot>
        </button>
      </div>
      <div class="tabs-header-actions" v-if="$slots.header">
        <slot name="header" :activeTab="activeTab" :tabs="tabs"></slot>
      </div>
    </div>
    <div class="tabs-content">
      <div 
        v-for="tab in tabs"
        :key="tab.id"
        v-show="activeTab === tab.id"
        class="tab-panel">
        <slot :name="`tab-panel-${tab.id}`" :tab="tab" :isActive="activeTab === tab.id">
          <!-- Default empty content -->
        </slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue'

interface Tab {
  id: string
  label: string
  data?: any
}

export default defineComponent({
  name: 'Tabs',
  props: {
    tabs: {
      type: Array as () => Tab[],
      required: true
    },
    initialTab: {
      type: String,
      required: true
    }
  },
  emits: ['tab-change'],
  setup(props, { emit }) {
    const activeTab = ref(props.initialTab)

    const switchTab = (tabId: string): void => {
      activeTab.value = tabId
      emit('tab-change', tabId)
    }

    // Watch for changes to initialTab prop
    watch(() => props.initialTab, (newTab) => {
      activeTab.value = newTab
    })

    return {
      activeTab,
      switchTab
    }
  }
})
</script>

<style scoped>
.tabs-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.tabs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #e0e0e0;
  background: #f9f9f9;
  gap: 0;
}

.tabs-header-tabs {
  display: flex;
  gap: 0;
}

.tabs-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 16px;
}

.tab-button {
  padding: 12px 24px;
  background: transparent;
  border: none;
  border-bottom: 3px solid transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #666;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
}

.tab-button:hover {
  background: #f0f0f0;
  color: #333;
}

.tab-button.active {
  color: #333;
  border-bottom-color: #ff5e8a;
  background: white;
  font-weight: 600;
}

.tabs-content {
  padding: 20px;
}

.tab-panel {
  animation: fadeIn 0.2s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>

